import React from 'react';
import { createPortal } from 'react-dom';
import CreateAttachmentItemParameters from '../Domain/CreateAttachmentItemParameters';
import InputTypesEnum from '../Enums/InputTypesEnum';
import ValidityHelper from '../Helpers/ValidityHelper';
import AttachmentService from '../Services/AttachmentService';
import FigmaButton from './FigmaButton';
import ButtonColorsEnum from '../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../Enums/ButtonTypeEnum';
import IconPositionEnum from '../Enums/IconPositionEnum';
import ErrorMessage from './ErrorMessage';
import FaIcon from './FaIcon';
import Loading from './loading';
import InputWithClear from './InputComponents/InputWithClear';
import FileUploadField from './InputComponents/BaseInputs/FileUploadField';
import '../Css/AttachmentsComponent.css';

class AddNewAttachmentWindow extends React.Component {
  static defaultProps = {
    entityTypeId: 8,
    entityId: 22,
    isOverWindow: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      attachment: new CreateAttachmentItemParameters(props.entityTypeId, props.entityId),
    };
    this.handleEsc = this.handleEsc.bind(this);
  }

  componentDidMount() {
    if (this.props.isOverWindow) {
      document.addEventListener('keydown', this.handleEsc, false);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleEsc, false);
  }

  handleEsc(event) {
    if (event.keyCode === 27) {
      this.props.clickOnCancel(false);
    }
  }

  changeOfEditedItem(fieldName, newValue) {
    const newAttachmentItem = {
      ...this.state.attachment,
      [fieldName]: newValue,
    };
    this.setState({ attachment: newAttachmentItem });
  }

  // A field row that mirrors the edit wizard: label-left / control-right (isInOneRow)
  // inside a col-12 wrapper so the .EditPanelInput card gives it a divider.
  renderTextField(fieldName, labelText, type) {
    return (
      <div className='col-12'>
        <InputWithClear
          fieldName={fieldName}
          labelText={labelText}
          type={type}
          value={this.state.attachment[fieldName]}
          onChange={(name, newValue) => this.changeOfEditedItem(name, newValue)}
          validityChecker={ValidityHelper.forRequiredTextField}
          isInOneRow={true}
          margin='m-0'
        />
      </div>
    );
  }

  AllRulesAreFullfilled() {
    return (
      this.state.attachment.SelectedFile !== null &&
      this.state.attachment.Name &&
      this.state.attachment.Description &&
      !this.state.loading
    );
  }

  // File picker as a label-left / control-right row matching the text fields.
  // The drop-zone itself is the shared FileUploadField (ds-dropzone in bravo-app.css).
  renderFileUploadField() {
    const hasFile = !!this.state.attachment.SelectedFile;
    return (
      <div className='col-12'>
        <div className='row att-file-row'>
          <div className='col-3 wizzarddiv'>File</div>
          <div className='col-9'>
            <FileUploadField
              acceptedTypes='.png, .jpg, .doc, .docx, .csv, .xls, .xlsx, .pdf, .ppt, .pptx, .msg'
              onChange={(file) => this.changeOfEditedItem('SelectedFile', file)}
            />
            {hasFile ? null : <div className='att-drop-required'>You have to select a file!</div>}
          </div>
        </div>
      </div>
    );
  }

  onSubmit() {
    if (!this.AllRulesAreFullfilled()) return;

    const data = new FormData();
    data.append('file', this.state.attachment.SelectedFile);
    data.append('Name', this.state.attachment.Name);
    data.append('Description', this.state.attachment.Description);
    data.append('EntityTypeId', this.state.attachment.EntityTypeId.toString());
    data.append('EntityId', this.state.attachment.EntityId.toString());
    this.setState({ loading: true });
    AttachmentService.PostAttachmentFile(data)
      .then((result) => {
        if (result.error) {
          ErrorMessage.SetErrorMessage(result.error);
        }
        this.props.clickOnCancel(true);
      })
      .catch((error) => {
        ErrorMessage.SetErrorMessage('Error! ErrorCode: ANAW149');
        this.setState({ loading: false });
      });
  }

  renderContent() {
    return (
      <form
        className='att-modal-form'
        onSubmit={(e) => {
          e.preventDefault();
          this.onSubmit();
        }}
      >
        {/* Reuse the edit-wizard body classes so the modal mirrors that panel:
            surface-2 body wrapping a bordered inner card with divided rows. */}
        <div className='EditPanelInput'>
          <div className='container-fluid'>
            <div className='row'>
              {this.renderTextField('Name', 'Attachment Name', InputTypesEnum.Text)}
              {this.renderFileUploadField()}
              {this.renderTextField('Description', 'Description', InputTypesEnum.TextBox)}
            </div>
          </div>
        </div>
        <div className='EditBoxFooter'>
          <div className='EditBoxFooterInfo'>
            {!this.AllRulesAreFullfilled() && !this.state.loading && (
              <span className='EditBoxFooterWarning'>
                <FaIcon iconName='fa-solid fa-triangle-exclamation' /> Complete the required fields
              </span>
            )}
          </div>
          <div className='EditBoxFooterRight'>
            <FigmaButton
              label='Cancel'
              iconName='fa-solid fa-xmark'
              iconPosition={IconPositionEnum.Left}
              color={ButtonColorsEnum.Common}
              type={ButtonTypeEnum.NormalNormal}
              onClick={() => this.props.clickOnCancel(false)}
            />
            <FigmaButton
              label='Add new Attachment'
              iconName={`fa-solid fa-upload${this.state.loading ? ' fa-spin' : ''}`}
              iconPosition={IconPositionEnum.Left}
              color={ButtonColorsEnum.Primary}
              type={ButtonTypeEnum.NormalNormal}
              submit={true}
              disabled={!this.AllRulesAreFullfilled()}
            />
          </div>
        </div>
      </form>
    );
  }

  render() {
    if (this.props.isOverWindow) {
      // Portal to <body> so the fixed-position modal escapes the detail popup's
      // stacking / containing block (.top-of-boxFigma uses overflow:hidden + zoom),
      // letting it center against the viewport instead of the popup's top-left.
      return createPortal(
        <div
          className='bravo-ds'
          data-theme={document.documentElement.getAttribute('data-theme') || undefined}
        >
          <div className='modal-wrap att-modal-wrap'>
            <div className='modal attachments-add att-modal'>
              <div className='modal-head'>
                <span className='mh-icon'>
                  <FaIcon iconName='fa-solid fa-paperclip' />
                </span>
                <h2>Add New Attachment</h2>
                <span className='sp' />
                <button
                  type='button'
                  className='close'
                  title='Cancel'
                  onClick={() => this.props.clickOnCancel(false)}
                >
                  <FaIcon iconName='fa-solid fa-xmark' />
                </button>
              </div>
              {this.state.loading ? (
                <div className='att-modal-loading'>
                  <Loading />
                </div>
              ) : (
                this.renderContent()
              )}
            </div>
          </div>
        </div>,
        document.body
      );
    }

    return <div className='attachments-add p-1 m-1'>{this.state.loading ? <Loading /> : this.renderContent()}</div>;
  }
}

export default AddNewAttachmentWindow;
